import React, { useEffect, useState } from 'react';
import { isTiffFile, useTiffPreview } from '../utils/tiffPreview';

// Shared by LoyaltyReceipts.js (the review queue) and
// UnclaimedLoyaltyReceipts.js (held receipts, somo-circle1-api#691) — both
// show the same receipt files, so they share one thumbnail and one preview
// modal rather than two copies of the PDF/TIFF handling below.

// Mirrors EditLoyaltyReceipt.js's own isImageFile helper. Upload screens only
// accept jpeg/png/pdf (ACCEPTED_FILE_TYPES there), but this queue also surfaces
// older/email-sourced receipts that predate that restriction — e.g. HEIC — so
// "not an image" can't be assumed to mean "is a PDF" here; each is checked
// explicitly and anything else falls back to a generic "no preview" state
// rather than being force-fed into the PDF viewer.
//
// Always called against fileUrl (or preview.url, which IS a receipt's
// fileUrl), never fileName — somo-circle1-api#667: a successfully-converted
// HEIC/HEIF receipt keeps its ORIGINAL fileName (e.g. "receipt.heic") for
// display/audit even after s3Key/fileUrl are repointed to the converted
// .jpg. fileExtension works fine against a full URL — .split('.').pop()
// grabs the last dot-separated segment regardless of how many other dots
// appear earlier in the domain, so "https://bucket.s3.../receipt.jpg"
// correctly yields "jpg" (fileUrl here is always a clean path with no query
// string — buildFileUrl/buildAttachmentFileUrl on the API side never
// append one).
const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png']);
const fileExtension = (name) => (name || '').split('.').pop()?.toLowerCase();
export const isImageFile = (name) => IMAGE_EXTENSIONS.has(fileExtension(name));
export const isPdfFile = (name) => fileExtension(name) === 'pdf';

// "%PDF-" — every real PDF starts with this. A `.pdf`-named object that's
// actually something else at rest (an error page the storage layer returned
// with a 200, a corrupted/truncated upload) still passes isPdfFile() above,
// so the fetched bytes get checked here too: without this, forcing that data
// to Blob type application/pdf just hands the browser's PDF viewer garbage,
// and it draws its own generic "failed to load PDF document" icon instead of
// this screen's own "couldn't load a preview" fallback ever showing.
const PDF_MAGIC_BYTES = [0x25, 0x50, 0x44, 0x46, 0x2d];
const looksLikePdf = (buffer) => {
    const head = new Uint8Array(buffer.slice(0, PDF_MAGIC_BYTES.length));
    return PDF_MAGIC_BYTES.every((byte, i) => head[i] === byte);
};

// Accessible name for a thumbnail button — an image thumbnail is already
// named by its alt text, so this is only needed for the icon fallbacks.
export const thumbnailAriaLabel = (fileUrl) => (isImageFile(fileUrl)
    ? undefined
    : `${isPdfFile(fileUrl) ? 'PDF' : isTiffFile(fileUrl) ? 'TIFF' : 'File'} receipt thumbnail`);

// 48px queue thumbnail. A JPEG/PNG renders straight from its URL; a TIFF has no
// native browser renderer so it's decoded to a PNG client-side (useTiffPreview,
// cached per URL) and shown once ready — a spinner while decoding, and the
// file-type icon if the file is a PDF/HEIC or the TIFF decode fails.
export function ReceiptThumbnail({ fileUrl }) {
    const tiff = useTiffPreview(isTiffFile(fileUrl) ? fileUrl : null);
    const thumbStyle = {
        width: 48, height: 48, objectFit: 'cover', borderRadius: 4,
    };

    if (isImageFile(fileUrl)) {
        return (
            <img
                src={fileUrl}
                alt="Receipt thumbnail"
                style={thumbStyle}
                onError={(e) => { e.target.style.display = 'none'; }}
            />
        );
    }

    if (isTiffFile(fileUrl) && tiff.status === 'ready') {
        return <img src={tiff.src} alt="Receipt thumbnail" style={thumbStyle} />;
    }

    return (
        <div
            className="d-flex align-items-center justify-content-center"
            style={{
                width: 48, height: 48, borderRadius: 4, border: '1px dashed var(--grey2)',
            }}
        >
            {isTiffFile(fileUrl) && tiff.status === 'loading' ? (
                <span className="spinner-border spinner-border-sm text-secondary" role="status" aria-hidden="true" />
            ) : (
                <i
                    className={`fa-solid ${isPdfFile(fileUrl) ? 'fa-file-pdf' : isTiffFile(fileUrl) ? 'fa-file-image' : 'fa-file'}`}
                    style={{ fontSize: '1.5rem', color: isPdfFile(fileUrl) ? '#dc3545' : 'var(--grey2)' }}
                />
            )}
        </div>
    );
}

// Receipt preview (image or PDF) — `preview` is { url, label } or null.
// Opens on top of the table or another modal (higher z-index than
// Bootstrap's default 1050/1040 modal/backdrop) so a receipt can be
// inspected full-size without leaving the page or losing work in progress.
export function ReceiptPreviewModal({ preview, onClose }) {
    const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null); // blob: URL for the PDF branch below
    const [pdfPreviewError, setPdfPreviewError] = useState(false);

    // Browsers can't render TIFF in an <img>, so a .tiff receipt is decoded to
    // a PNG client-side for the preview modal (isImageFile/isPdfFile both stay
    // false for it — see IMAGE_EXTENSIONS). Passthrough for every other type.
    const tiffPreview = useTiffPreview(preview?.url);

    // Receipts are stored with Content-Disposition: attachment, so pointing
    // an <iframe> straight at the file URL makes the browser download it
    // instead of rendering it (blank frame + a file in the downloads tray).
    // Fetching the bytes ourselves and handing the iframe a blob: URL sidesteps
    // that header entirely, since a blob URL carries none of the original
    // response headers.
    //
    // A blob: URL is same-origin with this app (unlike the original cross-origin
    // fileUrl), so the bytes must be forced to application/pdf regardless of
    // what the response's Content-Type says — otherwise a receipt upload that's
    // actually HTML/JS (mislabeled, or a validation gap in the upload API) would
    // render in this frame with this app's own origin instead of the storage
    // bucket's harmless one. This alone closes that risk: the browser dispatches
    // a blob: URL to a renderer based on the Blob's declared type, not by
    // sniffing/executing the bytes as HTML — see the iframe below for why it's
    // deliberately *not* also sandboxed.
    useEffect(() => {
        if (!preview || !isPdfFile(preview.url)) {
            setPdfPreviewUrl(null);
            setPdfPreviewError(false);
            return undefined;
        }
        let objectUrl;
        let cancelled = false;
        setPdfPreviewUrl(null);
        setPdfPreviewError(false);
        fetch(preview.url)
            .then((res) => {
                if (!res.ok) throw new Error(`Failed to fetch PDF: ${res.status}`);
                return res.arrayBuffer();
            })
            .then((buffer) => {
                if (cancelled) return;
                if (!looksLikePdf(buffer)) throw new Error('Fetched file is not a valid PDF');
                objectUrl = URL.createObjectURL(new Blob([buffer], { type: 'application/pdf' }));
                setPdfPreviewUrl(objectUrl);
            })
            .catch(() => {
                if (!cancelled) setPdfPreviewError(true);
            });
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [preview]);

    if (!preview) return null;

    return (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 1060 }}>
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">{preview.label}</h5>
                        <button type="button" className="close" onClick={onClose}>
                            <span>&times;</span>
                        </button>
                    </div>
                    <div className="modal-body text-center">
                        {isImageFile(preview.url) ? (
                            <img
                                src={preview.url}
                                alt={preview.label}
                                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                            />
                        ) : isTiffFile(preview.url) ? (
                            // TIFF has no native browser renderer — decoded to a PNG
                            // client-side (useTiffPreview above).
                            tiffPreview.status === 'error' ? (
                                <div className="py-5">
                                    <i className="fa-solid fa-file-image" style={{ fontSize: '3rem', color: 'var(--grey2)' }} />
                                    <p className="text-muted mt-2 mb-0">
                                        Couldn&apos;t render this TIFF here. Use &quot;Open original in new tab&quot; below.
                                    </p>
                                </div>
                            ) : tiffPreview.status === 'ready' ? (
                                <img
                                    src={tiffPreview.src}
                                    alt={preview.label}
                                    style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                                />
                            ) : (
                                <div className="py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="sr-only">Loading preview...</span>
                                    </div>
                                </div>
                            )
                        ) : !isPdfFile(preview.url) ? (
                            // Neither a recognized image nor a PDF (e.g. HEIC from an older/
                            // email-sourced receipt) — no in-app renderer for this, and no
                            // point fetching it just to fail. Straight to "use the real file".
                            <div className="py-5">
                                <i className="fa-solid fa-file" style={{ fontSize: '3rem', color: 'var(--grey2)' }} />
                                <p className="text-muted mt-2 mb-0">
                                    No in-app preview available for this file type. Use &quot;Open original in new tab&quot; below.
                                </p>
                            </div>
                        ) : pdfPreviewError ? (
                            <div className="py-5">
                                <i className="fa-solid fa-file-pdf" style={{ fontSize: '3rem', color: '#dc3545' }} />
                                <p className="text-muted mt-2 mb-0">
                                    Couldn&apos;t load a preview for this PDF. Use &quot;Open original in new tab&quot; below.
                                </p>
                            </div>
                        ) : pdfPreviewUrl ? (
                            // No sandbox attribute here: Chrome's built-in PDF viewer is an
                            // internal extension (MimeHandlerView) that Chromium refuses to
                            // activate inside a sandboxed iframe, which silently breaks the
                            // preview (shows a generic "blocked content" icon) even for a
                            // valid PDF. Not needed for safety anyway — the browser dispatches
                            // a blob: URL to its PDF renderer based on the Blob's declared
                            // `type` (forced to application/pdf above), not by executing the
                            // content as HTML, so this can't become a same-origin XSS vector.
                            <iframe
                                src={pdfPreviewUrl}
                                title={preview.label}
                                style={{ width: '100%', height: '70vh', border: '1px solid var(--grey2)' }}
                            />
                        ) : (
                            <div className="py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="sr-only">Loading preview...</span>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <a
                            href={preview.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-outline-secondary"
                        >
                            Open original in new tab
                        </a>
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
